/**
 * Site-level webhook helpers (ENG-2233 / SUP-757).
 *
 * A site can own any number of WebhookCustomer rows; `GET
 * webhook_customer/?customer=<id>` returns all of them unpaginated. The Site
 * About tab shows one row per webhook and edits them independently, so the
 * pending edits are kept per webhook id rather than as one flat patch.
 */

import { getDefaultAlarmName } from "../../../../utils/getDefaultAlarmName";
import {
  WebhookFieldErrors,
  buildWebhookAuthCreatePayload,
} from "../../../../utils/webhookAuth";
import type { SiteWebhookFormValues } from "../../../../common/SiteWebhookFormFields";

export interface SiteWebhook {
  id: number;
  customer: number;
  display_name: string;
  webhook_type: string;
  target_url: string;
  method: string;
  auth_type: string | null;
  auth_endpoint: string | null;
  auth_body: string | null;
  auth_token_path: string | null;
  message_body: string;
}

/** webhook id -> the fields changed on that row (only the diff is PATCHed). */
export type WebhooksPatchMap = Record<number, Record<string, any>>;

/** webhook id -> server 400 messages for that row. */
export type WebhookFieldErrorsById = Record<number, WebhookFieldErrors>;

export const WEBHOOKS_DEFAULT_ALARM_NAME = "Webhooks";

/**
 * Whether Site About shows the Webhooks tab (and its Add button).
 *
 * `siteInfo.webhooks` is the Django-admin `admin_features` checkbox, which
 * nothing sets automatically — a site onboarded with Default Alarm = Webhooks
 * usually has it off — so the tab also shows when the default alarm is
 * Webhooks or the site already has a webhook (same signals the camera page
 * uses for its Webhooks section).
 */
export const shouldShowWebhooksTab = (
  siteInfo: { webhooks?: boolean; default_alarm?: number | string | null },
  webhooksInfo: unknown[] | null | undefined
): boolean => {
  if (siteInfo?.webhooks) return true;
  if (
    getDefaultAlarmName(siteInfo?.default_alarm) === WEBHOOKS_DEFAULT_ALARM_NAME
  ) {
    return true;
  }
  return Array.isArray(webhooksInfo) && webhooksInfo.length > 0;
};

/** Add / edit a site webhook rides the same permission as the camera-side button. */
export const canEditSiteWebhooks = (): boolean => {
  try {
    const userInfo = JSON.parse(localStorage.userInfo ?? "null");
    return Boolean(userInfo?.permission?.edit_cameras);
  } catch {
    return false;
  }
};

/** Replace the row with the same id, or append when the id is new. */
export const mergeWebhookById = (
  webhooks: SiteWebhook[],
  webhook: SiteWebhook
): SiteWebhook[] => {
  const index = webhooks.findIndex((candidate) => candidate.id === webhook.id);
  if (index === -1) return [...webhooks, webhook];
  const next = [...webhooks];
  next[index] = webhook;
  return next;
};

/** Pending entries that actually carry a change. */
export const pendingWebhookPatches = (
  patchMap: WebhooksPatchMap | null | undefined
): Array<[number, Record<string, any>]> =>
  Object.entries(patchMap ?? {})
    .filter(([, patch]) => patch && Object.keys(patch).length > 0)
    .map(([id, patch]) => [Number(id), patch]);

/** Client-side check for a JSON text field; null when fine or blank. */
export const jsonTextError = (
  value: string | null | undefined,
  label: string
): string | null => {
  if (!value || !value.trim()) return null;
  try {
    JSON.parse(value);
    return null;
  } catch {
    return `${label} must be valid JSON.`;
  }
};

/** POST body for a new site webhook (the server sets the type to Custom). */
export const buildSiteWebhookCreatePayload = (
  siteId: number,
  values: SiteWebhookFormValues
) => {
  const { auth, ...rest } = values;
  return {
    customer: siteId,
    ...rest,
    ...buildWebhookAuthCreatePayload(auth),
  };
};
